import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
const featuredImg = "https://media.lacontessina.com/site/src/assets/projects/la-fin-bel-air.webp";
const VIDEO_ID = "3bNgyOSNH_U";

export default function FeaturedProject() {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setZoomed(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: t("featured.title"),
    description: t("featured.description"),
    thumbnailUrl: [`https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`],
    uploadDate: "2023-01-01",
    embedUrl: `https://www.youtube.com/embed/${VIDEO_ID}`,
    contentUrl: `https://youtu.be/${VIDEO_ID}`,
  };

  return (
    <section
      className="bg-[hsl(0_0%_7%)] text-white py-20 lg:py-32"
      style={{
        contentVisibility: "auto",
        containIntrinsicSize: "1200px",
      }}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="lc-container-ext">
        <div className="max-w-3xl">
          <span className="text-bronze text-[11px] tracking-[0.3em] uppercase">
            {t("featured.eyebrow")}
          </span>
          <h2 className="mt-5 font-display font-light text-[36px] md:text-[56px] lg:text-[72px] leading-[1.02] tracking-[-0.03em]">
            {t("featured.title")}
          </h2>
          <p className="mt-7 text-white/70 text-[15px] md:text-[16px] leading-[1.8] max-w-2xl">
            {t("featured.description")}
          </p>
        </div>

        <div
          ref={frameRef}
          className="relative mt-12 lg:mt-16 rounded-[24px] overflow-hidden aspect-video bg-black group"
          style={{
            contain: "paint",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
          }}
        >
          {playing ? (
            <iframe
              className="absolute inset-0 h-full w-full border-0"
              src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
              title={t("featured.title")}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              <img
                src={featuredImg}
                alt={t("featured.title")}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  transform: zoomed ? "scale(1.08) translateZ(0)" : "scale(1.0) translateZ(0)",
                  transition: "transform 7000ms ease-out",
                  backfaceVisibility: "hidden",
                  willChange: "transform",
                }}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/50" />
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label={t("featured.play")}
                className="absolute inset-0 flex items-center justify-center"
              >
                <span className="flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-full bg-white/95 text-[hsl(0_0%_7%)] transition-transform group-hover:scale-105 shadow-2xl">
                  <Play className="h-7 w-7 md:h-9 md:w-9 ml-1 fill-current" />
                </span>
              </button>
              <a
                href={`https://youtu.be/${VIDEO_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="absolute bottom-5 right-5 md:bottom-7 md:right-7 inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/40 backdrop-blur px-5 py-2.5 text-[12px] tracking-wide text-white hover:bg-black/60 transition"
              >
                {t("featured.watch_youtube")}
              </a>
            </>
          )}
        </div>
      </div>
    </section>
  );
}